import React from 'react';

import type { EstadoDaSincronizacao } from '../../../api/comprasDashboardApi';
import { duracaoDesde } from './CarimboSync';
// ⚠️ Importados explicitamente, e não herdados do bundle: contar com o CSS que outra tela carregou
// é o acoplamento invisível do `BUG-V74` — o dia em que aquela tela mudar de import, a faixa aqui
// aparece sem estilo, sem erro em lugar nenhum.
import '../../../shared/ui/dashboard-base.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * O aviso de que as leituras estão pausadas, ou de que rodam uma vez ao dia (at-108).
 *
 * Existe como componente próprio porque aparece nas **duas abas** do painel — e o texto não pode
 * divergir entre elas: são a mesma informação, e duas redações acabariam dizendo coisas
 * ligeiramente diferentes sobre o mesmo estado.
 *
 * 🔴 **Nunca `erro-de-tela`.** Aquela classe é o vermelho de "esta tela falhou ao carregar", e
 * pausa não é falha: é o sistema fazendo exatamente o que o ADMIN mandou. Dar a ela a silhueta de
 * tela quebrada é a receita para ensinar quem lê a ignorar o vermelho que importa (a mesma lição do
 * selo "Fora do horário", at-103).
 *
 * ⚠️ Estado ausente (backend anterior a esta entrega) **não desenha nada** — e é o certo: a
 * ausência do bloco significa "ritmo normal, não pausado", que é o estado sobre o qual não há o que
 * avisar.
 */

export function formatarDataHora(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

interface Props {
  sincronizacao?: EstadoDaSincronizacao;
  /** A idade do dado mais fresco do painel, para a faixa de pausa dizer o que está na tela. */
  ultimoDadoEm?: string | null;
}

export default function FaixaEstadoDaSincronizacao({ sincronizacao, ultimoDadoEm }: Props) {
  if (!sincronizacao) return null;

  if (sincronizacao.pausado) {
    const quando = formatarDataHora(sincronizacao.pausadoEm);
    // Construção neutra de gênero: o sistema não guarda o gênero de quem está no cadastro, e
    // "Fulana pausou" concordado no masculino é lido como defeito (regra da at-62).
    const autoria = sincronizacao.pausadoPor
      ? ` por ${sincronizacao.pausadoPor.nome}`
      : '';
    const idade = duracaoDesde(ultimoDadoEm ?? null);

    return (
      <p className="aviso-de-tela">
        A sincronização com o Nasajon está <strong>pausada</strong>
        {quando ? ` desde ${quando}` : ''}
        {autoria}. Os números abaixo são os do último dado recebido
        {idade ? `, ${idade}` : ''}, e não mudam enquanto a pausa durar. Para retomar, vá em
        Administração do Sistema, na tela de Compras.
      </p>
    );
  }

  if (sincronizacao.modo === 'DIARIO') {
    return (
      <p className="do-nota-janela">
        Neste período o painel é atualizado <strong>uma vez ao dia</strong>, às{' '}
        {sincronizacao.horaDaLeituraDiaria}h. Durante o dia os números não mudam — é uma escolha de
        economia, não uma falha. Se o sistema estiver fora do ar nesse horário, a leitura acontece
        assim que ele voltar.
      </p>
    );
  }

  return null;
}
